import React, { useEffect, useState, useCallback, useRef } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { parse } from "query-string";
import Header from "colbi_web_ui/lib/components/layout/Header/Header";
import I18n from "../../I18n";
import { mapProperties } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    downloadAction,
    removeModal,
    upgradeAccount,
    handleDownloadFilesAction,
    getOrganizationDashboardSummary,
    requestDataForOrganizationsAction,
} from "../../store/actions";

import { LoadingIndicator } from "colbi_web_ui/lib/components";
import proxy from "../../routes/proxy";
import { useAuth } from "react-oidc-context";
import styles from "./OrganizationsProjects.module.sass";

import { UserRoles } from "../../enums/userRoles";
import { submitTicketFormData } from "../../store/actions/modalActions";
import { REACT_APP_AUTH_MODE } from "../../environmentVariables";

import { BuildSovosNavProps, decodeUserSession, redirectNotValid } from "../../utils/utils";
import { Dashboard } from "mosaic-react-icons";
import { selectObjectById } from "../../utils/mapProjectsByOrganization";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { getDefaultYearType } from "../../components/AggregationData/YearSelector";
import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import AggregateDataWidget from "../../components/AggregationData/AggregationDataWidget";
import AggregateDataList from "../../components/AggregationData/AggregateDataList";

import GlobalLayout from "../../components/GlobalLayout";
import OrganizationalReportButton from "../../components/AggregationData/OrganizationalReportButton";
import { getLocale } from "../../utils/locale";
import ModalHeaderCloseCustom from "../../components/ModalHeaderCloseCustom/ModalHeaderCloseCustom";

const OrganizationsProjects = (props) => {
    const {
        user,
        fetch,
        match,
        history,
        location,
        pushModal,
        pushDialog,
        route,
        upgradeAccount,
        getOrganizationDashboardSummary,
        requestDataForOrganizations,
        reload,
    } = props;

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const organizationId = match.params.organizationId;
    const auth = useAuth();
    const dispatch = useDispatch();

    const locale = getLocale(auth, match, true);

    const i18n = I18n.use(locale);
    const userInformation = decodeUserSession(user);
    const ssoEmailSent = window.localStorage.getItem("ssoEmailSent") === "true";
    const appVersion = fetch?.results[`${locale}_application_version`]?.version;

    const [isModalTicketOpen, setIsModalTicketOpen] = useState(false);

    // eslint-disable-next-line
    const [emailSent, _] = useState(ssoEmailSent);

    const [selectedYearType, setSelectedYearType] = useState(getDefaultYearType(params.filter));
    const [selectedYear, setSelectedYear] = useState(null);

    const checkUpgrade = useCallback(() => {
        if (
            pushDialog &&
            userInformation &&
            !userInformation.hasSovosIdentity &&
            !userInformation.hasUpgradedAccount &&
            organizationId &&
            !emailSent
        ) {
            pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                title: i18n`sso_upgrade_title`,
                alert: true,
                cancelEvent: false,
                text: [
                    i18n`sso_upgrade_text`,

                    i18n`sso_upgrade_transition`,

                    i18n`sso_upgrade_confirm`,
                ],
            })
                .then(() => {
                    upgradeAccount().then(({ message }) => {
                        window.localStorage.setItem("ssoEmailSent", "true");
                        pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                            title: message,

                            text: [
                                i18n`sso_account_upgraded`,

                                i18n`sso_account_upgraded_email`,

                                i18n`sso_account_upgraded_grateful`,
                            ],

                            alert: true,

                            cancel: () => {},
                        })
                            .then(() => null)

                            .catch(() => null);
                    });
                })

                .catch(() => null);
        } else if (
            pushDialog &&
            auth &&
            !auth?.isLoading &&
            !auth?.isAuthenticated &&
            userInformation &&
            userInformation.hasSovosIdentity &&
            !userInformation.hasUpgradedAccount &&
            organizationId &&
            !emailSent
        ) {
            pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                title: i18n`sso_upgrade_header`,

                text: [
                    i18n`sso_confirmation_reminder`,

                    i18n`sso_confirmation_reprompt`,

                    i18n`sso_confirmation_reprompt_warning`,
                ],
            })
                .then(() => {
                    window.localStorage.setItem("ssoEmailSent", "true");
                    upgradeAccount().then(({ message }) => {
                        pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                            title: message,

                            text: [
                                i18n`sso_account_upgraded`,

                                i18n`sso_account_upgraded_email`,

                                i18n`sso_account_upgraded_grateful`,
                            ],

                            alert: true,

                            cancel: () => {},
                        })
                            .then(() => null)

                            .catch(() => null);
                    });
                })

                .catch(() => {
                    window.localStorage.setItem("ssoEmailSent", "true");
                    return null;
                });
        }
    }, [pushDialog, userInformation, auth, i18n, organizationId, upgradeAccount, emailSent]);

    useEffect(() => {
        if (
            userInformation &&
            checkAccessByRole(userInformation, [UserRoles.PP_SUPPORT, UserRoles.SUPPORT]) &&
            !isModalTicketOpen
        ) {
            setIsModalTicketOpen(true);
            pushModal({
                title: "Ticket Submission",
                data: {},
                args: "",
                view: "TicketForm",
                modalType: "center",
                customHeaderButton: (props) => (
                    <ModalHeaderCloseCustom
                        {...props}
                        title={"Ticket Submission"}
                        closeButtonAction={() => dispatch(logout(auth))}
                    />
                ),
            }).catch(() => null);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(userInformation)]);

    const checkUpgradeRef = useRef(checkUpgrade);

    useEffect(() => {
        if (REACT_APP_AUTH_MODE === "SSO") {
            checkUpgradeRef.current();
        }
    }, [locale, i18n, auth?.isLoading]);

    //Data for menu
    const { menuOrganizationProject = [] } = mapProperties(
        {
            menuOrganizationProject: "query(0).list",
        },
        fetch.results,
        fetch.status,
        OrganizationsProjects.queries(params),
        props,
        params,
        i18n,
        locale,
    );

    //results for the list
    const { organizationProjects, filter } = mapProperties(
        {
            organizationProjects: "query(0)",
            filter: "param(filter)",
        },
        fetch.results,
        fetch.status,
        route.queries(params),
        props,
        params,
        i18n,
        locale,
    );

    //filters to show
    const { resultFilters = [] } =
        (organizationId &&
            mapProperties(
                { resultFilters: "query(1).list" },
                fetch.results,
                fetch.status,
                route.queries(params),
                props,
                params,
                i18n,
                locale,
            )) ||
        {};

    const organizationModules =
        fetch.results[`${locale}_get_organization_cache_organizationId_${organizationId}`]
            ?.modules || [];

    const { maxFiscalYear: organizationMaxFiscalYear } = organizationProjects || {};

    const organizationSelectedInMenu = selectObjectById(menuOrganizationProject, organizationId);

    const { accounts, selectedAccount } = cleanPropertiesNestedInMenu(
        menuOrganizationProject,
        organizationSelectedInMenu,
    );

    const isLoading =
        (fetch.activity === null || fetch.activity) &&
        !organizationId &&
        !selectedYearType &&
        !selectedYear;

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    if (redirect) {
        return redirect;
    }

    if (
        !organizationId &&
        userInformation.organizationsRoles &&
        !checkAccessByRole(userInformation, [UserRoles.PP_SUPPORT, UserRoles.SUPPORT])
    ) {
        const orgs = Object.keys(userInformation.organizationsRoles || {});
        return <Redirect to={`/${locale}/organizations/${orgs.length ? orgs[0] : ""}`} />;
    }

    location.state = {
        ...location.state,
        selectedLink: i18n("projects"),
    };

    const link = {
        label: i18n("projects"),
        icon: Dashboard,
        action: () => {},
    };
    const staticLinks = [link];

    const navProps = BuildSovosNavProps(
        i18n("projects"),
        accounts,
        selectedAccount,
        { locale, history, proxy, params },
        userInformation,
        staticLinks || [],
    );
    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries: [],
        params,
        propsFiscalYear: "",
        propsImportId: "",
        repositoryStatus: "",
        styles,
        version: appVersion,
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <main className={styles["main"]}>
                <Header
                    title={organizationProjects && organizationProjects.name}
                    name={userInformation.name}
                    headerButtonComponents={
                        organizationModules.includes("aggregate_org_data")
                            ? [
                                  <OrganizationalReportButton
                                      key="organizationalReportButton"
                                      pushModal={pushModal}
                                      i18n={i18n}
                                      organizationId={organizationId}
                                      reload={reload}
                                      pushDialog={pushDialog}
                                      user={userInformation}
                                      requestDataForOrganizations={requestDataForOrganizations}
                                  />,
                              ]
                            : []
                    }
                />
                {isLoading ? (
                    <LoadingIndicator className={styles["loading-indicator"]} />
                ) : (
                    <div className={styles["content"]}>
                        <div className={styles["org-heat-map-container"]}>
                            <>
                                <AggregateDataWidget
                                    getOrganizationDashboardSummary={
                                        getOrganizationDashboardSummary
                                    }
                                    organizationId={organizationId}
                                    selectedYearType={selectedYearType}
                                    selectedYear={selectedYear}
                                    setSelectedYearType={setSelectedYearType}
                                    setSelectedYear={setSelectedYear}
                                    params={params}
                                    organizationModules={organizationModules}
                                    i18n={i18n}
                                    organizationMaxFiscalYear={organizationMaxFiscalYear}
                                    filter={filter}
                                />
                                <AggregateDataList
                                    year={selectedYear}
                                    yearType={selectedYearType}
                                    organizationProjects={organizationProjects}
                                    filter={filter}
                                    i18n={i18n}
                                    params={params}
                                    organizationModules={organizationModules}
                                    resultFilters={resultFilters}
                                />
                            </>
                        </div>
                    </div>
                )}
            </main>
        </GlobalLayout>
    );
};

OrganizationsProjects.queries = (param) => {
    return [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "application_version",
            body: "version",
        },
        {
            resource: "get_organization_cache",
            body: "modules",
            args: {
                organizationId: param.organizationId,
            },
        },
    ];
};

export default connect(
    ({ user, fetch, uploads }) => ({
        user,
        fetch,
        uploads,
    }),
    {
        logout,
        pushDialog,
        pushModal,
        uploadFiles: uploadAction,
        submitTicket: submitTicketFormData,
        download: downloadAction,
        downloadFile: handleDownloadFilesAction,
        submitAuditNote: auditNoteSubmit,
        requestDataForOrganizations: requestDataForOrganizationsAction,
        popModal,
        toggleMenu,
        setLocale,
        removeModal,
        upgradeAccount,
        getOrganizationDashboardSummary,
    },
)(withRouter(OrganizationsProjects));
